import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { internalTransfersApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";


/**
 * UC-M1-INV-07 — Internal transfer detail. Two-phase ledger flow:
 *   * Dispatch (Draft/Submitted/Approved → Posted) writes TRANSFER_OUT + IN_TRANSIT_OUT.
 *   * Receive (Posted; partial allowed) writes IN_TRANSIT_IN + TRANSFER_IN per received qty.
 *   The list stays in Posted state until every line is fully received.
 */
export function InternalTransferDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canManage = useAuth(s => s.hasPermission("inventory.transfer.internal"));
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [received, setReceived] = useState<Record<string, number>>({});

  const query = useQuery({
    queryKey: ["internal-transfer", id],
    queryFn: () => internalTransfersApi.get(id),
    enabled: !!id,
  });

  const dispatchMutation = useMutation({
    mutationFn: () => internalTransfersApi.dispatch(id, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["internal-transfer", id] });
      qc.invalidateQueries({ queryKey: ["internal-transfers"] });
      setDispatchOpen(false);
    },
  });

  const receiveMutation = useMutation({
    mutationFn: () => internalTransfersApi.receive(id, {
      lines: Object.entries(received)
        .filter(([, qty]) => qty > 0)
        .map(([lineId, receivedQty]) => ({ lineId, receivedQty })),
    }, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["internal-transfer", id] });
      qc.invalidateQueries({ queryKey: ["internal-transfers"] });
      setReceived({});
      setReceiveOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const t = query.data;
  const isDispatched = t.status === "Posted";
  const remainingByLine = Object.fromEntries(t.lines.map(l => [l.id, l.qty - l.receivedQty]));
  const fullyReceived = t.lines.every(l => l.receivedQty >= l.qty);
  const hasReceiveQty = Object.values(received).some(v => v > 0);
  const allReceiveQtysValid = Object.entries(received).every(([lineId, v]) =>
    v >= 0 && v <= (remainingByLine[lineId] ?? 0));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{t.documentNo}</Typography>
          <StatusPill label={t.status} />
          {isDispatched && !fullyReceived && (
            <Chip size="small" variant="outlined" color="info" label={`In transit ${t.inTransitQty}`} />
          )}
          {fullyReceived && isDispatched && (
            <Chip size="small" color="success" label="Fully received" />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          {!isDispatched && canManage && (
            <Button variant="contained" startIcon={<LocalShippingIcon />}
              onClick={() => setDispatchOpen(true)}>Dispatch</Button>
          )}
          {isDispatched && !fullyReceived && canManage && (
            <Button variant="contained" color="success" startIcon={<MoveToInboxIcon />}
              onClick={() => {
                setReceived(Object.fromEntries(t.lines.map(l => [l.id, l.qty - l.receivedQty])));
                setReceiveOpen(true);
              }}>Receive</Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Source</Typography>
            <Typography>{t.sourceWarehouseName}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Destination</Typography>
            <Typography>{t.destWarehouseName}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{formatDate(t.documentDate)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography>{t.reason ?? "—"}</Typography></Stack>
        </Stack>
        {t.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{t.notes}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell align="right">Sent</TableCell>
            <TableCell align="right">In transit</TableCell>
            <TableCell align="right">Received</TableCell>
            <TableCell align="right">Remaining</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {t.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell>{l.batchNo ?? "—"}</TableCell>
                <TableCell align="right">{l.qty}</TableCell>
                <TableCell align="right">{l.inTransitQty}</TableCell>
                <TableCell align="right">{l.receivedQty}</TableCell>
                <TableCell align="right">{l.qty - l.receivedQty}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/inventory/internal-transfers")}>Back to list</Button>

      {/* Dispatch dialog */}
      <Dialog open={dispatchOpen} onClose={() => setDispatchOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setDispatchOpen(false)}>Dispatch transfer {t.documentNo}?</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Dispatching writes a TRANSFER_OUT and an IN_TRANSIT_OUT ledger row at the source
            warehouse for every line. The destination doesn't pick up the stock until you
            tap Receive — partial receipts are allowed.
          </DialogContentText>
          {dispatchMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(dispatchMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={dispatchMutation.isPending}
            startIcon={dispatchMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />}
            onClick={() => dispatchMutation.mutate()}>Dispatch</Button>
        </DialogActions>
      </Dialog>

      {/* Receive dialog */}
      <Dialog open={receiveOpen} onClose={() => setReceiveOpen(false)} maxWidth="md" fullWidth>
        <ClosableDialogTitle onClose={() => setReceiveOpen(false)}>Receive transfer {t.documentNo}</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the qty received per line at the destination. Lines partially received
            stay In Transit; you can come back and receive the remainder later.
          </DialogContentText>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Receive qty</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {t.lines.map(l => {
                const remaining = l.qty - l.receivedQty;
                if (remaining <= 0) return null;
                return (
                  <TableRow key={l.id}>
                    <TableCell>{l.itemName} <Typography variant="caption" color="text.secondary">{l.sku}</Typography></TableCell>
                    <TableCell>{l.batchNo ?? "—"}</TableCell>
                    <TableCell align="right">{remaining}</TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" sx={{ width: 120 }}
                        value={received[l.id] ?? 0}
                        onChange={(e) => setReceived(prev => ({ ...prev, [l.id]: Number(e.target.value) || 0 }))}
                        inputProps={{ min: 0, max: remaining, step: "0.001", style: { textAlign: "right" } }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {receiveMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(receiveMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={!hasReceiveQty || !allReceiveQtysValid || receiveMutation.isPending}
            startIcon={receiveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <MoveToInboxIcon />}
            onClick={() => receiveMutation.mutate()}>Confirm receipt</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
